import type { StoreActionApi } from 'react-sweet-state';
import { fireOperationalAnalyticsWithContext } from '@atlassian/jira-ui-modifications-analytics';
import type { FieldChangesMapPublic } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common';
import type {
	StoreContainerProps,
	IssueAdjustmentsState,
	AppId,
	ChangeId,
	BufferForChangeId,
} from '../../../types';
import { UiModificationsFieldsValidationError } from '../../../utils/errors';
import { setAppErrors } from '../../app-errors';
import { setError } from '../../errors';
import { unregisterAllFieldsOfChangeId, unregisterFields } from '../../register-fields';
import { updateFields } from '../../update-fields';
import { clearBufferForChangeId } from '../clear-buffer';
import { addBufferError, resetBufferErrors, setAppConflictErrors } from '../errors';

import { sendBufferConflictMetrics, sendBufferFinalMetrics } from '../metrics';
import type { BufferContext } from '../types';
import { getConflicts } from '../utils';

export const validateNoConflictAndApply =
	(bufferForChangeId: BufferForChangeId, changeId: ChangeId, context: BufferContext) =>
	async (
		{ dispatch }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType, createAnalyticsEvent }: StoreContainerProps,
	) => {
		const conflicts = getConflicts({ bufferForChangeId });

		const applyBufferedChangesSequentially = async () => {
			for (const [appId, fieldsChanges] of Object.entries(bufferForChangeId)) {
				if (fieldsChanges !== null) {
					// In general await should not be used inside loops, because it changes parallel processing
					// of promises to sequential. In our case sequential processing is desired.
					// Without it, 2nd buffered app changes override 1st buffered app changes.
					// With sequential processing, appliedChanges are saved in UIM store before next changes are processed.
					// eslint-disable-next-line no-await-in-loop
					await applyAppChanges(fieldsChanges, appId);
				}
			}
		};

		const applyAppChanges = async (fieldsChanges: FieldChangesMapPublic, appId: AppId) => {
			const { cloudId, activationId, issueId, environment, getAsyncLookupData, transitionId } =
				context;
			const lookupDataFromApi = await getAsyncLookupData(fieldsChanges);

			try {
				await dispatch(
					updateFields({
						changeId,
						fieldsChanges,
						lookupDataFromApi,
						tenantContext: { cloudId, activationId },
						viewContext: { issueId, transitionId },
						environment,
						appId,
					}),
				);
			} catch (err) {
				if (err instanceof UiModificationsFieldsValidationError) {
					fireOperationalAnalyticsWithContext(
						createAnalyticsEvent({}),
						'fieldsChangesValidation failed',
						viewType,
						{ ...err.attributes },
					);

					dispatch(setError({ errorCode: 'APPLY_BUFFER_CHANGES_ERROR' }));
					dispatch(addBufferError('APPLY_BUFFER_CHANGES_ERROR'));

					// Setting app errors from transformers to pass them to the onErrorCallback
					dispatch(setAppErrors({ errors: err.attributes.errors, appId }));
					return;
				}
				throw err;
			} finally {
				dispatch(unregisterFields({ changeId, appId }));
			}
		};

		if (Object.keys(conflicts).length > 0) {
			dispatch(setError({ errorCode: 'MULTI_APP_CONFLICT' }));
			dispatch(addBufferError('MULTI_APP_CONFLICT'));
			dispatch(sendBufferConflictMetrics(changeId, bufferForChangeId, conflicts));
			dispatch(setAppConflictErrors(changeId, bufferForChangeId, conflicts));
		} else {
			await applyBufferedChangesSequentially();
			dispatch(sendBufferFinalMetrics(changeId, bufferForChangeId));
		}

		dispatch(unregisterAllFieldsOfChangeId(changeId));
		dispatch(clearBufferForChangeId({ changeId }));
		dispatch(resetBufferErrors());
	};
